import { createServerFn } from "@tanstack/react-start";
import { createAuthenticatedApiConfig, throwApiError, hasServerDeletePermission } from "@/lib/utils.server";
import { isForbiddenError } from "@/lib/utils";
import { getIvrs, createIvr, getIvr, updateIvr, deleteIvr } from "@/lib/mma/sdk.gen";

export const getIVRsFn = createServerFn({ method: "GET" }).handler(async (): Promise<any[]> => {
  const config = await createAuthenticatedApiConfig();
  const response = await getIvrs(config);

  if (!response.data) {
    return await throwApiError(response, "Failed to get IVRs", response.response?.status);
  }
  const ivrs = response?.data?.data || [];

  const hasDeletePermissionIvr = await hasServerDeletePermission("IVR");

  return ivrs
    .filter((ivr) => ivr.id != null && ivr.name != null)
    .map((ivr) => ({
      id: ivr.id!,
      name: ivr.name!,
      href: `/ivr/${ivr.id}`,
      dropdown: [
        ...(hasDeletePermissionIvr
          ? [
              {
                id: `${ivr.id}-delete`,
                name: "Delete",
                onClick: "delete",
                color: "red-500",
                parent: { name: ivr.name, id: ivr.id },
              },
            ]
          : []),
      ],
    }));
});

export const ivrQueryOptions = {
  queryKey: ["ivr"],
  queryFn: getIVRsFn,
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
  refetchOnReconnect: false,
};

export const createIVRFn = createServerFn({ method: "POST" })
  .inputValidator((data: any) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await createIvr({
      ...config,
      body: data,
    });

    const err = (response as any).error;
    if (err) {
      console.error("[IVR] create error:", JSON.stringify(err));
      return await throwApiError(err, "Failed to create IVR", response.response?.status);
    }
    return response.data ?? null;
  });

export const fetchIVR = createServerFn({ method: "GET" })
  .inputValidator((d: { ivrId: string }) => d)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await getIvr({
      ...config,
      path: {
        id: Number(data.ivrId),
      },
    });
    return response.data?.data ?? null;
  });

export const ivrDetailQueryOptions = (ivrId: string) => ({
  queryKey: ["ivr", ivrId],
  queryFn: () => fetchIVR({ data: { ivrId } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
  refetchOnReconnect: false,
});

export const updateIVRFn = createServerFn({ method: "POST" })
  .inputValidator((data: { ivrId: string; body: any }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await updateIvr({
      ...config,
      path: { id: Number(data.ivrId) },
      body: data.body,
    });
    const err = (response as any).error;
    if (err) {
      console.error(`[IVR] update error for id=${data.ivrId}:`, JSON.stringify(err));
      return await throwApiError(err, "Failed to update IVR", response.response?.status);
    }
    return response.data ?? null;
  });

export const deleteIVRFn = createServerFn({ method: "POST" })
  .inputValidator((data: { ivrId: string }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await deleteIvr({
      ...config,
      path: { id: Number(data.ivrId) },
    });
    if (!response.data) {
      return await throwApiError(response, "Failed to delete IVR", response.response?.status);
    }
    return { success: true };
  });
